import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
    Check,
    Download,
    ExternalLink,
    FileText,
    Loader2,
    RefreshCw,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { crud, whatsappMerkez } from "@/lib/api";
import { Button } from "@/components/ui/button";

function extOf(name = "") {
    const i = name.lastIndexOf(".");
    return i >= 0 ? name.slice(i + 1).toLowerCase() : "";
}

function previewKind(filename, mediaTuru, blobType) {
    const ext = extOf(filename);
    const mime = (blobType || "").toLowerCase();
    if (mime.startsWith("image/") || ["jpg", "jpeg", "png", "webp", "gif", "bmp"].includes(ext)) {
        return "image";
    }
    if (mime === "application/pdf" || ext === "pdf") return "pdf";
    if (["doc", "docx", "word"].includes(ext) || mime.includes("word")) return "word";
    if (["xls", "xlsx"].includes(ext) || mime.includes("sheet") || mime.includes("excel")) {
        return "excel";
    }
    if (mediaTuru === "document" || ext) return "document";
    return "unknown";
}

export default function WhatsAppEvrakOnizlemeModal({
    open,
    inboxId,
    mesaj,
    onClose,
    onDone,
}) {
    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState(null);
    const [blobUrl, setBlobUrl] = useState("");
    const [blobType, setBlobType] = useState("");
    const [filename, setFilename] = useState("");
    const [dosyaArama, setDosyaArama] = useState("");
    const [dosyaAdaylar, setDosyaAdaylar] = useState([]);
    const [seciliDosyaId, setSeciliDosyaId] = useState("");
    const [busy, setBusy] = useState(false);

    const es = meta?.eslestirme || mesaj?.evrak_eslestirme || {};
    const eksikList = meta?.eksik_evrak_list || [];
    const eksikEslesme = meta?.eksik_eslesme;

    const loadPreview = useCallback(
        async (retry = false) => {
            if (!inboxId) return;
            setLoading(true);
            let objectUrl = "";
            try {
                const data = await whatsappMerkez.inboxEslestirmeOnizle(inboxId);
                setMeta(data);
                const fn = data.filename || data.eslestirme?.filename || "whatsapp-belge";
                setFilename(fn);
                setSeciliDosyaId(String(data.eslestirme?.dosya_id || mesaj?.sigorta_dosyasi_id || ""));
                setDosyaArama(data.eslestirme?.dosya_no || data.eslestirme?.plaka || "");

                try {
                    const blob = await whatsappMerkez.inboxBekleyenMedyaBlob(inboxId, retry ? { retry: 1 } : {});
                    objectUrl = URL.createObjectURL(blob);
                    setBlobUrl(objectUrl);
                    setBlobType(blob.type || "");
                } catch {
                    setBlobUrl("");
                    setBlobType("");
                }
            } catch {
                toast.error("Önizleme yüklenemedi.");
            } finally {
                setLoading(false);
            }
            return () => {
                if (objectUrl) URL.revokeObjectURL(objectUrl);
            };
        },
        [inboxId, mesaj?.sigorta_dosyasi_id],
    );

    useEffect(() => {
        if (!open || !inboxId) {
            setMeta(null);
            setBlobUrl("");
            setBlobType("");
            return;
        }
        let cleanup;
        loadPreview().then((fn) => {
            cleanup = fn;
        });
        return () => {
            if (cleanup) cleanup();
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, inboxId]);

    const kind = useMemo(
        () => previewKind(filename, meta?.media_turu || mesaj?.media_turu, blobType),
        [filename, meta?.media_turu, mesaj?.media_turu, blobType],
    );

    async function handleOnayla() {
        if (!inboxId || busy) return;
        const dosyaId = seciliDosyaId || (es.dosya_id ? String(es.dosya_id) : "");
        if (!dosyaId) {
            toast.error("Hedef dosya seçin.");
            return;
        }
        setBusy(true);
        try {
            if (mesaj?.evrak_otomasyon_durumu === "ONAY_BEKLIYOR") {
                await whatsappMerkez.inboxOnayla(inboxId, {
                    dosya_id: Number(dosyaId),
                    evrak_turu: eksikEslesme?.kod || es.evrak_turu,
                });
            } else {
                await whatsappMerkez.inboxBagla(inboxId, { dosya_id: Number(dosyaId) });
                await whatsappMerkez.inboxEvrakYukle(inboxId, { dosya_id: Number(dosyaId) });
            }
            toast.success("Evrak dosyaya yüklendi.");
            onDone?.();
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Yükleme başarısız.");
        } finally {
            setBusy(false);
        }
    }

    if (!open) return null;

    return createPortal(
        <div className="wa-evrak-onizle" role="dialog" aria-label="Evrak önizleme">
            <button type="button" className="wa-evrak-onizle__backdrop" onClick={onClose} aria-label="Kapat" />
            <div className="wa-evrak-onizle__panel">
                <header className="wa-evrak-onizle__header">
                    <div>
                        <h3>Evrak önizleme</h3>
                        <p className="text-xs text-[#8696a0]">{filename || "Belge"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="wa-evrak-onizle__icon-btn"
                            title="Yeniden indir"
                            onClick={() => loadPreview(true)}
                        >
                            <RefreshCw className="h-4 w-4" />
                        </button>
                        <button type="button" className="wa-evrak-onizle__icon-btn" onClick={onClose}>
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </header>

                <div className="wa-evrak-onizle__body">
                    <div className="wa-evrak-onizle__viewer">
                        {loading ? (
                            <div className="wa-evrak-onizle__empty">
                                <Loader2 className="h-8 w-8 animate-spin text-sky-400" />
                            </div>
                        ) : !blobUrl ? (
                            <div className="wa-evrak-onizle__empty">
                                <FileText className="h-10 w-10 text-[#8696a0] mb-2" />
                                <p className="text-sm text-[#8696a0]">Önizleme için medya indirilemedi.</p>
                                <Button size="sm" variant="outline" className="mt-3" onClick={() => loadPreview(true)}>
                                    Tekrar dene
                                </Button>
                            </div>
                        ) : kind === "pdf" ? (
                            <iframe title="PDF önizleme" src={blobUrl} className="wa-evrak-onizle__frame" />
                        ) : kind === "image" ? (
                            <img src={blobUrl} alt="Evrak" className="wa-evrak-onizle__image" />
                        ) : (
                            <div className="wa-evrak-onizle__file-card">
                                <FileText className="h-12 w-12 text-sky-300" />
                                <div className="text-sm font-medium">{filename}</div>
                                <div className="text-xs text-[#8696a0]">
                                    {kind === "word"
                                        ? "Word belgesi — tarayıcıda doğrudan açılamaz"
                                        : kind === "excel"
                                          ? "Excel dosyası — indirip açın"
                                          : "Belge dosyası"}
                                </div>
                                {meta?.eslestirme?.ocr_preview ? (
                                    <pre className="wa-evrak-onizle__ocr">{meta.eslestirme.ocr_preview}</pre>
                                ) : null}
                                <a
                                    href={blobUrl}
                                    download={filename || "evrak"}
                                    className="wa-evrak-onizle__download"
                                >
                                    <Download className="h-4 w-4" /> İndir / aç
                                </a>
                            </div>
                        )}
                    </div>

                    <aside className="wa-evrak-onizle__side">
                        <div className="wa-evrak-onizle__meta">
                            <div><span>Müvekkil</span><strong>{es.muvekkil_adi || mesaj?.muvekkil_adi || "—"}</strong></div>
                            <div><span>Dosya</span><strong>{es.dosya_no || mesaj?.dosya_no || "—"}</strong></div>
                            <div><span>Plaka</span><strong>{es.plaka || "—"}</strong></div>
                            <div><span>Tür tahmini</span><strong>{es.evrak_turu || eksikEslesme?.kod || "—"}</strong></div>
                        </div>

                        {eksikEslesme ? (
                            <div className="wa-evrak-onizle__eksik-hit">
                                <Check className="h-4 w-4 text-emerald-400" />
                                Eksik evrak eşleşmesi: <strong>{eksikEslesme.ad}</strong>
                                <span className="block text-[10px] opacity-80 mt-1">
                                    Bu belge eksik listede — müvekkil numarasından geldiyse otomatik yüklenir.
                                </span>
                            </div>
                        ) : null}

                        {eksikList.length > 0 ? (
                            <div className="wa-evrak-onizle__eksik-list">
                                <div className="text-xs font-semibold text-amber-300 mb-2">Eksik evraklar</div>
                                {eksikList.map((e) => (
                                    <span key={e.kod} className="wa-evrak-onizle__eksik-chip">
                                        {e.ad}
                                    </span>
                                ))}
                            </div>
                        ) : null}

                        <label className="text-xs text-[#8696a0]">Dosya seç / değiştir</label>
                        <input
                            type="search"
                            value={dosyaArama}
                            placeholder="MBS-… plaka müvekkil"
                            onChange={async (e) => {
                                const q = e.target.value;
                                setDosyaArama(q);
                                if (q.trim().length < 2) {
                                    setDosyaAdaylar([]);
                                    return;
                                }
                                try {
                                    const res = await crud.list("sigorta-dosyalari", {
                                        search: q.trim(),
                                        page_size: 8,
                                    });
                                    setDosyaAdaylar(res.results || res || []);
                                } catch {
                                    setDosyaAdaylar([]);
                                }
                            }}
                        />
                        {dosyaAdaylar.length > 0 && (
                            <select value={seciliDosyaId} onChange={(e) => setSeciliDosyaId(e.target.value)}>
                                <option value="">Dosya seçin…</option>
                                {dosyaAdaylar.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {(d.dosya_no || `#${d.id}`) +
                                            (d.plaka ? ` · ${d.plaka}` : "") +
                                            (d.muvekkil_ad_soyad ? ` · ${d.muvekkil_ad_soyad}` : "")}
                                    </option>
                                ))}
                            </select>
                        )}

                        <div className="wa-evrak-onizle__actions">
                            <Button variant="outline" onClick={onClose} disabled={busy}>
                                Kapat
                            </Button>
                            <Button
                                className="bg-emerald-600 hover:bg-emerald-500 text-white flex-1"
                                disabled={busy || !(seciliDosyaId || es.dosya_id)}
                                onClick={handleOnayla}
                            >
                                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ExternalLink className="h-4 w-4 mr-1" />}
                                Dosyaya ekle
                            </Button>
                        </div>
                    </aside>
                </div>
            </div>
        </div>,
        document.body,
    );
}
